import { HTMLAttributes, useEffect, useRef } from 'react'

import Media from '@/components/base/media'
import { isCorporate } from '@/constants'

import { cn } from '@/lib/utils'
import { WPImage, WPVideo } from '@/types'

export type PlyrProps = {
  image: WPImage
  poster: WPImage
  video: WPVideo
  youtube: string
  use_video: boolean
  is_autoplay: boolean
  video_type: 'mp4' | 'youtube'
  image_fit: 'cover' | 'contain'
}

export type MediaPlyrProps = HTMLAttributes<HTMLDivElement> & Partial<PlyrProps>

const MediaPlyr = ({
  poster,
  image,
  video,
  youtube,
  use_video,
  video_type,
  is_autoplay,
  image_fit = 'cover',
  className,
  ...props
}: MediaPlyrProps) => {
  const youtubeRef = useRef<HTMLDivElement>(null!)
  const videoRef = useRef<HTMLVideoElement>(null!)
  const player = useRef<Plyr>(null!)

  useEffect(() => {
    if (typeof window === 'undefined' || !use_video) return

    const target = youtubeRef.current || videoRef.current
    if (!target) return

    if (video_type === 'youtube' || !is_autoplay) {
      import('plyr').then((PlyrModule) => {
        if (video_type === 'youtube' || !is_autoplay) {
          player.current = new PlyrModule.default(
            videoRef.current || youtubeRef.current,
            {
              loadSprite: false,
              youtube: {
                noCookie: true
              }
            }
          )
        }
        player.current = new PlyrModule.default(target, {
          loadSprite: false,
          youtube: { noCookie: true }
        })
      })
    }

    return () => {
      if (player.current) {
        player.current.destroy()
        player.current = null!
      }
    }
  }, [use_video, video_type, is_autoplay, youtube])

  return (
    <>
      <style jsx global>{`
        :root {
          --plyr-color-main: ${isCorporate ? '#004c59' : '#4d4d4d'};
        }
        .plyr {
          transform: scale(1.01);
        }
      `}</style>

      {use_video ? (
        <div className={cn('relative overflow-hidden', className)} {...props}>
          {video_type === 'youtube' ? (
            <div
              ref={youtubeRef}
              className="iub-no-block"
              data-plyr-provider="youtube"
              data-plyr-embed-id={youtube}
              data-poster={poster?.url}
            />
          ) : (
            <video
              ref={videoRef}
              loop={is_autoplay}
              muted={is_autoplay}
              autoPlay={is_autoplay}
              data-poster={poster?.url}
              playsInline={true}
              preload="auto"
            >
              <source src={video?.url} type="video/mp4" />
            </video>
          )}
        </div>
      ) : (
        <Media use_video={false} image={image} fill fit={image_fit} />
      )}
    </>
  )
}

export default MediaPlyr
